import { useEffect, useState } from 'react';

/**
 * Returns true once `ref`'s scroll container has been scrolled to the bottom —
 * or immediately if its content isn't tall enough to scroll (so callers never
 * dead-end on short content). Re-arms whenever `resetKey` changes (pass the
 * wizard step); on re-arm the container is scrolled back to the top.
 *
 * The "scrolled to bottom" state latches true for the current `resetKey`, so
 * async content growth or a viewport resize won't revoke an enable the user
 * already earned. The "fits" check stays live.
 */
export function useScrolledToBottom(
  ref: React.RefObject<HTMLElement | null>,
  resetKey: unknown,
): boolean {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    setDone(false);
    el.scrollTop = 0;

    const check = () => {
      const fits = el.scrollHeight <= el.clientHeight + 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      if (fits || atBottom) setDone(true);
    };

    check();
    el.addEventListener('scroll', check, { passive: true });

    // Body: viewport resize. First child: async content growth within the step.
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);

    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [ref, resetKey]);

  return done;
}
